import React from 'react';
import {Box, Chip, Stack, Tooltip} from '@mui/material';
import {normalizeEeiPriority} from '../../../firebase/firestore/eei';
import {COLLECTION_EEI_STATUS} from '../coverage/buildCollectionCoverageView';
import {computePirStatus, PIR_STATUS, resolvePirEeis} from '../pirPayload';

export function matchAnalyticEeiRow(detailCoverage, eei, index) {
    const rows = detailCoverage?.eeis || [];
    if (eei?.id) {
        const byId = rows.find((row) => row.eei?.id === eei.id);
        if (byId) return byId;
    }
    if (eei?.eeiNumber) {
        const byNumber = rows.find((row) => row.eei?.eeiNumber === eei.eeiNumber);
        if (byNumber) return byNumber;
    }
    return rows[index] || null;
}

export function matchCollectionEeiSegment(segments, eei, index) {
    const eeiIndex = index + 1;
    if (eei?.id) {
        const byId = segments.find((row) => row.eeiId === eei.id);
        if (byId) return byId;
    }
    if (eei?.eeiNumber) {
        const byNumber = segments.find((row) => row.eeiNumber === eei.eeiNumber);
        if (byNumber) return byNumber;
    }
    return segments.find((row) => row.eeiIndex === eeiIndex) || segments[index] || null;
}

export function analyticAnswerBadge(status, coveragePct) {
    // Missing score (no question row) is N/A — same as CoverageBar — not "Not answered".
    if (coveragePct == null && (status == null || status === PIR_STATUS.UNCOVERED)) {
        return {label: 'N/A', color: 'default', tooltip: 'No analytic coverage yet'};
    }
    if (status === PIR_STATUS.COVERED) {
        return {label: 'Answered', color: 'success', tooltip: null};
    }
    if (status === PIR_STATUS.PARTIAL) {
        const tooltip = coveragePct != null
            ? `${Math.round(Number(coveragePct))}% analytic coverage`
            : 'Partially answered';
        return {label: 'Partially answered', color: 'warning', tooltip};
    }
    return {label: 'Not answered', color: 'error', tooltip: null};
}

export function collectionStatusBadge(status, linkedScorePct) {
    if (status == null || (status === COLLECTION_EEI_STATUS.UNCOVERED && linkedScorePct == null)) {
        return {label: 'N/A', color: 'default', tooltip: 'No collection coverage yet'};
    }
    if (status === COLLECTION_EEI_STATUS.COVERED) {
        return {label: 'Collected', color: 'success', tooltip: null};
    }
    if (status === COLLECTION_EEI_STATUS.PARTIAL) {
        const tooltip = linkedScorePct != null
            ? `${Math.round(Number(linkedScorePct))}% collection coverage`
            : 'Partially collected';
        return {label: 'Partially collected', color: 'warning', tooltip};
    }
    return {label: 'Not collected', color: 'error', tooltip: null};
}

/** True when analytic coverage is Answered and collection coverage is Collected. */
export function isEeiFullyResolved(analyticStatus, collectionStatus) {
    return analyticStatus === PIR_STATUS.COVERED
        && collectionStatus === COLLECTION_EEI_STATUS.COVERED;
}

const MUTED_BADGE_SX = {
    color: 'text.disabled',
    borderColor: 'divider',
    bgcolor: 'transparent',
};

/** Fixed widths match the longest labels so shorter statuses stay centered with equal side padding. */
export const COLLECTION_STATUS_BADGE_WIDTH = '21ch'; // Partially collected
export const ANSWER_STATUS_BADGE_WIDTH = '20ch'; // Partially answered

function StatusBadge({label, color, tooltip, muted = false, width}) {
    const chip = (
        <Chip
            label={label}
            size="small"
            color={muted ? 'default' : color}
            variant="outlined"
            sx={{
                height: 20,
                width,
                maxWidth: '100%',
                flexShrink: 0,
                fontSize: '0.6rem',
                fontWeight: muted ? 500 : 600,
                justifyContent: 'center',
                '& .MuiChip-label': {
                    px: 0.5,
                    py: 0,
                    width: '100%',
                    textAlign: 'center',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                },
                ...(muted ? MUTED_BADGE_SX : null),
            }}
        />
    );
    if (tooltip) {
        return (
            <Tooltip title={tooltip} arrow>
                {chip}
            </Tooltip>
        );
    }
    return chip;
}

export function AnalyticAnswerStatusBadge({analyticStatus, analyticPct = null, muted = false}) {
    const answer = analyticAnswerBadge(analyticStatus, analyticPct);
    return (
        <Box component="span" sx={{verticalAlign: 'middle', display: 'inline-flex'}}>
            <StatusBadge {...answer} muted={muted} width={ANSWER_STATUS_BADGE_WIDTH}/>
        </Box>
    );
}

export function CollectionStatusBadge({collectionStatus, collectionPct = null, muted = false}) {
    const collection = collectionStatusBadge(collectionStatus, collectionPct);
    return (
        <Box component="span" sx={{verticalAlign: 'middle', display: 'inline-flex'}}>
            <StatusBadge {...collection} muted={muted} width={COLLECTION_STATUS_BADGE_WIDTH}/>
        </Box>
    );
}

/**
 * Answer + collection status chips for an EEI row on ICR detail.
 * Default order: collected, then answered.
 */
export default function EeiCoverageStatusBadges({
    analyticStatus,
    analyticPct = null,
    collectionStatus,
    collectionPct = null,
}) {
    return (
        <Stack
            direction="row"
            spacing={0.5}
            component="span"
            sx={{verticalAlign: 'middle', display: 'inline-flex', mr: 1}}
            useFlexGap
        >
            <CollectionStatusBadge collectionStatus={collectionStatus} collectionPct={collectionPct}/>
            <AnalyticAnswerStatusBadge analyticStatus={analyticStatus} analyticPct={analyticPct}/>
        </Stack>
    );
}

/** Shared grid for all EEI rows so badge and ID columns align across rows. */
export const EEI_ROW_GRID_COLUMNS = '24px max-content 124px minmax(0, 1fr)';

/** First-line height for priority, badges, and ID cells (body1 × lineHeight 1.75). */
export const EEI_ROW_FIRST_LINE_HEIGHT = 28;

export const EEI_ROW_LEADING_CELL_SX = {
    display: 'flex',
    alignItems: 'center',
    minHeight: EEI_ROW_FIRST_LINE_HEIGHT,
};

/** Badge pair sits flush against the EEI ID column. */
export const EEI_ROW_BADGE_CELL_SX = {
    ...EEI_ROW_LEADING_CELL_SX,
    justifyContent: 'flex-end',
    pr: 0.5,
};

/** Collected + answered badges grouped tightly (collected first). */
export function EeiCoverageStatusBadgeGroup({
    analyticStatus,
    analyticPct = null,
    collectionStatus,
    collectionPct = null,
    muted: mutedProp,
}) {
    const muted = mutedProp ?? isEeiFullyResolved(analyticStatus, collectionStatus);
    return (
        <Stack
            direction="row"
            spacing={0.375}
            useFlexGap
            alignItems="center"
            sx={{display: 'inline-flex', flexShrink: 0, height: 20}}
        >
            <CollectionStatusBadge
                collectionStatus={collectionStatus}
                collectionPct={collectionPct}
                muted={muted}
            />
            <AnalyticAnswerStatusBadge
                analyticStatus={analyticStatus}
                analyticPct={analyticPct}
                muted={muted}
            />
        </Stack>
    );
}

/** @deprecated Use {@link EeiCoverageStatusBadgeGroup} */
export const EeiCoverageStatusBadgeColumns = EeiCoverageStatusBadgeGroup;

export function resolveEeiCoverageBadges(detailCoverage, collectionSegments, eei, index) {
    const analyticRow = matchAnalyticEeiRow(detailCoverage, eei, index);
    const collectionRow = matchCollectionEeiSegment(collectionSegments, eei, index);
    return {
        analyticStatus: analyticRow?.status || computePirStatus(null),
        analyticPct: analyticRow?.coveragePct ?? null,
        collectionStatus: collectionRow?.status || COLLECTION_EEI_STATUS.UNCOVERED,
        collectionPct: collectionRow?.linkedScorePct ?? null,
    };
}

const PRIORITY_SORT_KEY = {High: 0, Medium: 1, Low: 2};
const COLLECTION_SORT_KEY = {
    [COLLECTION_EEI_STATUS.UNCOVERED]: 0,
    [COLLECTION_EEI_STATUS.PARTIAL]: 1,
    [COLLECTION_EEI_STATUS.COVERED]: 2,
};
const ANSWER_SORT_KEY = {
    [PIR_STATUS.UNCOVERED]: 0,
    [PIR_STATUS.PARTIAL]: 1,
    [PIR_STATUS.COVERED]: 2,
};

function prioritySortKey(priority) {
    return PRIORITY_SORT_KEY[normalizeEeiPriority(priority)] ?? 1;
}

function collectionSortKey(status) {
    return COLLECTION_SORT_KEY[status] ?? 0;
}

function answerSortKey(status) {
    return ANSWER_SORT_KEY[status] ?? 0;
}

function eeiIdSortKey(eei) {
    return String(eei?.eeiNumber || eei?.id || '').toLowerCase();
}

/** Sort: priority DESC, collected ASC, answered ASC, id ASC. */
export function sortIcrEeiRows(rows) {
    return [...rows].sort((a, b) => {
        const byPriority = prioritySortKey(a.priority) - prioritySortKey(b.priority);
        if (byPriority !== 0) return byPriority;

        const byCollection = collectionSortKey(a.collectionStatus) - collectionSortKey(b.collectionStatus);
        if (byCollection !== 0) return byCollection;

        const byAnswer = answerSortKey(a.analyticStatus) - answerSortKey(b.analyticStatus);
        if (byAnswer !== 0) return byAnswer;

        return eeiIdSortKey(a.eei).localeCompare(eeiIdSortKey(b.eei));
    });
}

export function buildSortedIcrEeiRows(icr, detailCoverage, collectionSegments) {
    const eeis = resolvePirEeis(icr);
    const rows = eeis.map((eei, index) => ({
        eei,
        priority: eei.priority || 'Medium',
        ...resolveEeiCoverageBadges(detailCoverage, collectionSegments, eei, index),
    }));
    return sortIcrEeiRows(rows);
}
